import {
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { useSnackbar } from "src/components/snackbar";
import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Upload from "src/components/upload/upload";
import saveFill from "@iconify/icons-eva/save-outline";
import Icon from "@iconify/react";
import handleError from "src/utils/manejo-errores";
import { clientesApi } from "src/services/clientesApi";
import { useSelector } from "react-redux";

const MAX_SIZE_FILE = 50 * 1024 * 1024; //50 MB
const MAX_FILES = 1;

function mostrarAlerta(mensaje, enqueueSnackbar, color) {
  enqueueSnackbar(mensaje, {
    variant: color,
  });
}

const ModalOrganigramaGrupo = ({
  isOpen,
  setOpen,
  title,
  grupo: grupoSeleccionado,
  editar = false,
}) => {
  const [file, setFile] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [cargandoImagen, setCargandoImagen] = useState(false);
  const { sucursal } = useSelector((state) => state.auth.user);
  const { enqueueSnackbar } = useSnackbar();

  const [asociarOrganigramaGrupo] =
    clientesApi.useAsociarOrganigramaGrupoMutation();
  const [actualizarOrganigramaGrupo] =
    clientesApi.useActualizarOrganigramaGrupoMutation();
  const [generarUrl] =
    clientesApi.useLazyGenerarUrlPrefirmadaOrganigramaQuery();

  const handleDropSingleFile = useCallback((acceptedFiles) => {
    const newFile = acceptedFiles[0];
    if (newFile) {
      setFile(
        Object.assign(newFile, {
          preview: URL.createObjectURL(newFile),
        }),
      );
    } else {
      setFile(null);
    }
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const cargarImagenS3 = useCallback(async () => {
    if (!editar || !grupoSeleccionado) return;

    try {
      setCargandoImagen(true);
      const { grupo, s3key, urlArchivo } = grupoSeleccionado;

      let urlPreview = null;

      if (s3key) {
        // Registro con S3: obtener URL prefirmada
        const response = await generarUrl({
          nombreElemento: grupo,
          sucursal: sucursal.refSucursal,
        });

        if (response?.data?.url) {
          urlPreview = response.data.url;
        }
      } else if (urlArchivo) {
        // Registro antiguo con Dropbox: transformar a enlace directo (raw=1)
        urlPreview = urlArchivo.replace(/dl=0$/, "raw=1");
      }

      if (urlPreview) {
        // Usar la URL directamente como preview (evita problemas de CORS con S3)
        const nombrePreview = grupoSeleccionado.nombre_archivo || "organigrama";
        setFile({ preview: urlPreview, name: nombrePreview });
      }
    } catch (error) {
      console.error("Error cargando imagen:", error);
      mostrarAlerta(
        "Error al cargar la imagen. Reintente recargando la página",
        enqueueSnackbar,
        "error",
      );
    } finally {
      setCargandoImagen(false);
    }
  }, [editar, grupoSeleccionado, generarUrl, sucursal, enqueueSnackbar]);

  useEffect(() => {
    cargarImagenS3();
  }, [cargarImagenS3]);

  useEffect(() => {
    return () => {
      if (file?.preview && file.preview.startsWith("blob:")) {
        URL.revokeObjectURL(file.preview);
      }
    };
  }, [file]);

  const guardarNuevoOrganigrama = useCallback(
    async (grupo) => {
      const formData = new FormData();
      formData.append("grupo", grupo);
      formData.append("sucursal", sucursal.refSucursal);
      formData.append("archivo", file);

      const { data, error } = await asociarOrganigramaGrupo(formData);

      if (error) {
        mostrarAlerta(
          error?.data?.message || "Error al guardar organigrama",
          enqueueSnackbar,
          "error",
        );
        handleError("guardarNuevoOrganigrama", error);
      } else if (data) {
        mostrarAlerta(
          data.message || `El grupo ${grupo} ha sido agregado correctamente`,
          enqueueSnackbar,
          "success",
        );
        setOpen(false);
      }
    },
    [asociarOrganigramaGrupo, file, setOpen, enqueueSnackbar, sucursal],
  );

  const editarOrganigrama = useCallback(
    async (grupo) => {
      const formData = new FormData();
      formData.append("grupo", grupo);
      formData.append("sucursal", sucursal.refSucursal);
      formData.append("archivo", file);

      const { data, error } = await actualizarOrganigramaGrupo(formData);

      if (error) {
        mostrarAlerta(
          error?.data?.message || "Error al guardar organigrama",
          enqueueSnackbar,
          "error",
        );
        handleError("editarOrganigrama", error);
      } else if (data) {
        mostrarAlerta(
          data.message || `El grupo ${grupo} ha sido actualizado correctamente`,
          enqueueSnackbar,
          "success",
        );
        setOpen(false);
      }
    },
    [actualizarOrganigramaGrupo, file, setOpen, enqueueSnackbar, sucursal],
  );

  const guardarOrganigramaGrupo = useCallback(async () => {
    setGuardando(true);
    const { grupo } = grupoSeleccionado;

    try {
      if (!editar) {
        await guardarNuevoOrganigrama(grupo);
      } else {
        await editarOrganigrama(grupo);
      }
    } catch (error) {
      mostrarAlerta("Error en el servidor", enqueueSnackbar, "error");
      handleError("guardarOrganigramaGrupo", error);
    } finally {
      setGuardando(false);
    }
  }, [
    editar,
    editarOrganigrama,
    grupoSeleccionado,
    guardarNuevoOrganigrama,
    enqueueSnackbar,
  ]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="dialog-organigrama-grupo-clientes"
      id="dialog-organigrama-grupo-clientes"
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle
        style={styles.title}
        htmlFor="dialog-organigrama-grupo-clientes"
      >
        {title}
      </DialogTitle>
      <DialogContent>
        <Card>
          <CardHeader title={!editar ? "Subir archivo" : "Editar archivo"} />
          <CardContent>
            <Upload
              disabled={guardando}
              file={file}
              loading={cargandoImagen}
              onDrop={handleDropSingleFile}
              onDelete={() => setFile(null)}
              accept={{
                "image/jpeg": [],
                "image/png": [],
              }}
              maxSize={MAX_SIZE_FILE}
              maxFiles={MAX_FILES}
            />
          </CardContent>
        </Card>
      </DialogContent>
      <DialogActions>
        <LoadingButton onClick={handleClose} color="error" disabled={guardando}>
          Cerrar
        </LoadingButton>
        <LoadingButton
          disabled={!(file instanceof File) || guardando}
          startIcon={<Icon icon={saveFill} />}
          color="primary"
          onClick={guardarOrganigramaGrupo}
          loading={guardando}
        >
          Guardar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

ModalOrganigramaGrupo.propTypes = {
  isOpen: PropTypes.bool,
  setOpen: PropTypes.func,
  title: PropTypes.string,
  grupo: PropTypes.object,
  editar: PropTypes.bool,
};

const styles = {
  title: {
    textAlign: "center",
    fontWeight: "bold",
  },
};

export default React.memo(ModalOrganigramaGrupo);
